/* eslint-disable local/ensure-wac-token-usage */
import { ReactNode, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import { Link } from "@/libraries/design/components";
import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";
import { Cross } from "@/libraries/wpl/foundations/src/icons/basic/cross";
import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

/**
 * @deprecated - Please use @/libraries/design/components/banner-alert instead
 */
export interface BannerProps {
    identifier: string;
    text: string | ReactNode;
    linkUrl?: string;
    linkText?: string;
    dataEventProps?: DataEventProps;
    hasProductNavBar?: boolean;
}

const containerStyles = css({
    backgroundColor: "#deebff",
    position: "relative",
    paddingInlineEnd: token("space.300"),
    margin: "auto",
    display: "flex",
    zIndex: 1,
    [`&[data-dismissed="true"]`]: {
        display: "none"
    }
});

const contentStyles = css({
    "margin": "auto",
    "paddingInlineStart": token("space.400"),
    "paddingInlineEnd": token("space.800"),
    "display": "grid",
    "color": "#091e42",
    // Existing tech debt that should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "a": {
        // Existing tech debt that should be fixed
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        svg: {
            transition: "transform 0.25s ease-in-out"
        }
    },
    // Existing tech debt that should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "a:hover": {
        color: "#0065ff",
        textDecoration: "none",
        textShadow: "none",
        // Existing tech debt that should be fixed
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        svg: {
            transform: `translateX(${token("space.075")})`
        }
    }
});

const closeIconStyles = css({
    appearance: "none",
    background: "transparent",
    border: 0,
    cursor: "pointer",
    padding: 0,
    position: "absolute",
    insetInlineEnd: "30px",
    insetBlockStart: token("space.200"),
    // Existing tech debt that should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    svg: {
        color: "#505f79"
    }
});

/**
 * @deprecated - Please use @/libraries/design/components/banner-alert instead
 */
export const BannerAlert = ({
    identifier,
    text,
    linkText,
    linkUrl,
    dataEventProps,
    hasProductNavBar
}: BannerProps) => {
    const intl = useIntl();
    const bannerStorageItem = `BannerAlert_dismiss-${identifier}`;

    const translatedLabel = intl.formatMessage({
        id: "banner-alert.close.aria-label",
        defaultMessage: "Close"
    });

    const [isHidden, setIsHidden] = useState<boolean>(true);

    useEffect(() => {
        let dismissValue = null;

        dismissValue = getLocalStorage(bannerStorageItem);

        if (dismissValue !== "1") {
            setIsHidden(false);
        }
    }, [bannerStorageItem]);

    const dynamicStyles = hasProductNavBar ? { marginBlockStart: "60px" } : {};

    return (
        <UFOSegment name="banner-alert">
            <div
                css={containerStyles}
                data-dismissed={isHidden ? "true" : undefined}
            >
                <button
                    aria-label={translatedLabel}
                    css={closeIconStyles}
                    onClick={() => {
                        setIsHidden(true);

                        setLocalStorage(bannerStorageItem, "1");
                    }}
                >
                    <Cross size="20" />
                </button>

                <div css={contentStyles} style={dynamicStyles}>
                    <p>
                        <span>{text}</span>&nbsp;
                        {linkText && linkUrl && (
                            // eslint-disable-next-line @atlassian/a11y/anchor-has-content,@atlassian/a11y/link-name
                            <Link
                                text={linkText}
                                href={linkUrl}
                                type="inline"
                                dataEventProps={dataEventProps}
                            />
                        )}
                    </p>
                </div>
            </div>
        </UFOSegment>
    );
};
